import type { CommonProps } from '../../types';
import { type VariantProps, cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { Text } from '../../';
import { NEXT_LOCALES } from '@beat-venues/common-types';
import { useLayoutEffect, useRef, useState } from 'react';

export interface LanguageSwitcherProps
    extends CommonProps,
        VariantProps<typeof LanguageSwitcherStyles> {
    currentLanguage: string;
    supportedLanguages: typeof NEXT_LOCALES;
    changeLanguage: (language: string) => void;
}

export const LanguageSwitcherStyles = cva(
    'relative flex items-center gap-1 border border-white/20 rounded-full p-1 bg-gray-500/40'
);

export const LanguageSwitcher = ({
    className,
    currentLanguage,
    supportedLanguages = NEXT_LOCALES,
    changeLanguage,
    testId = 'language-switcher',
}: LanguageSwitcherProps): JSX.Element => {
    const languages: Record<string, string> = {
        nl: 'Nederlands',
        fr: 'Français',
        en: 'English',
    };

    const [indicator, setIndicator] = useState<{
        left: number;
        width: number;
    } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({});

    useLayoutEffect(() => {
        const measure = () => {
            const activeItem = itemRefs.current[currentLanguage];

            if (!activeItem) {
                return;
            }

            setIndicator({
                left: activeItem.offsetLeft,
                width: activeItem.offsetWidth,
            });
        };

        measure();

        // Custom fonts can swap in after this effect's initial layout pass,
        // shifting text widths and leaving the indicator sized for the
        // fallback font — re-measure whenever the switcher's layout changes.
        const container = containerRef.current;

        if (!container || typeof ResizeObserver === 'undefined') {
            return;
        }

        const observer = new ResizeObserver(measure);
        observer.observe(container);

        return () => observer.disconnect();
    }, [currentLanguage, supportedLanguages]);

    return (
        <div
            ref={containerRef}
            className={twMerge(LanguageSwitcherStyles({ className }))}
            data-testid={testId}
        >
            {indicator && (
                <span
                    aria-hidden="true"
                    className="bg-off-white-100 absolute inset-y-1 left-0 rounded-full transition-[transform,width] duration-300 ease-out"
                    style={{
                        transform: `translateX(${indicator.left}px)`,
                        width: indicator.width,
                    }}
                />
            )}
            {supportedLanguages.map((language) => {
                const isActive = currentLanguage === language;

                return (
                    <button
                        key={`btn-language-${language}`}
                        ref={(el) => {
                            itemRefs.current[language] = el;
                        }}
                        type="button"
                        className="relative z-10 flex h-7 min-w-7 items-center justify-center rounded-full px-4 py-1.5"
                        onClick={() => changeLanguage(language)}
                    >
                        <Text
                            className={`font-medium transition-colors duration-300 ${
                                isActive
                                    ? 'text-black'
                                    : 'text-white/60 hover:text-white'
                            }`}
                            size="sm"
                        >
                            <span className="sr-only">
                                {languages[language]}
                            </span>
                            <span aria-hidden="true">
                                {language.toUpperCase()}
                            </span>
                        </Text>
                    </button>
                );
            })}
        </div>
    );
};
